/** 页码使用 PDF 真实页序（从 1 开始），不使用纸面印刷页码。 */
export interface PageMarker { page: number; start: number }
export interface PageSegment extends PageMarker { end: number }
export interface SyncSource { key: string; sha256: string }
export interface RazSync {
  version: 1;
  level: "A";
  bookId: string;
  pdf: SyncSource & { pageCount: number };
  audio: SyncSource & { duration: number };
  segments: PageSegment[];
}
export function buildPageSegments(markers: PageMarker[], end: number, pageCount: number, duration: number): PageSegment[] {
  if (!Number.isInteger(pageCount) || pageCount < 1 || !Number.isFinite(duration) || duration <= 0) throw new Error("请等待绘本与音频加载完成");
  if (!markers.length) throw new Error("请先标记至少一页的开始时间");
  if (!Number.isFinite(end) || end <= 0 || end > duration + 0.05) throw new Error("请标记有效的朗读结束时间");
  return markers.map((marker, index) => {
    const next = markers[index + 1]?.start ?? end;
    if (!Number.isInteger(marker.page) || marker.page < 1 || marker.page > pageCount) throw new Error(`第 ${index + 1} 条 PDF 页码无效`);
    if (!Number.isFinite(marker.start) || marker.start < 0 || marker.start >= next || next > end) throw new Error(`第 ${index + 1} 条时间顺序错误：下一页必须晚于上一页`);
    return { page: marker.page, start: marker.start, end: next };
  });
}
/** 尚无同步区域（例如片头、片尾）返回 null。 */
export function pageAtTime(segments: PageSegment[], time: number): number | null {
  if (!Number.isFinite(time)) return null;
  return segments.find(segment => time >= segment.start && time < segment.end)?.page ?? null;
}

/** 只接受完整且互不重叠的时间轴；媒体一致性由调用方比较指纹。 */
export function parseRazSync(value: unknown): RazSync {
  if (!value || typeof value !== "object") throw new Error("时间轴格式错误");
  const sync = value as Partial<RazSync>;
  if (sync.version !== 1 || sync.level !== "A" || typeof sync.bookId !== "string" || !/^\d{2,}$/.test(sync.bookId) || !sync.pdf || !sync.audio || !Array.isArray(sync.segments) || !sync.segments.length) throw new Error("时间轴格式错误");
  if (sync.pdf.key !== `A/${sync.bookId}/book.pdf` || sync.audio.key !== `A/${sync.bookId}/audio.mp3` || !/^[a-f0-9]{64}$/.test(sync.pdf.sha256) || !/^[a-f0-9]{64}$/.test(sync.audio.sha256)) throw new Error("时间轴媒体信息错误");
  let previousEnd = 0;
  for (const segment of sync.segments) {
    if (!segment || typeof segment !== "object") throw new Error("时间轴片段错误");
    buildPageSegments([{ page: segment.page, start: segment.start }], segment.end, sync.pdf.pageCount, sync.audio.duration);
    if (segment.start < previousEnd) throw new Error("时间轴片段不能重叠");
    previousEnd = segment.end;
  }
  return sync as RazSync;
}
